import type { TableType } from '@bty/smartsheet'
import type { FieldItem } from '@/features/nodes/start'
import type { LLMChannels } from '@/apis/llm/model.ts'
import { request } from './lib'
import type { AuthAppDingTalk, AuthAppWeChat } from './agentgo'
import type { PartitionCategoryType } from './datastore/types'

export type AgentId = string

export type AgentRunStatus = 'SUCCEEDED' | 'FAILED'
export type AgentRunType =
  | 'AGENT_TESTING'
  | 'MULTIPLE_MODELS'
  | 'CHATBOT'
  | 'AGENT'
  | 'AGENT_OAPI'
  | 'COPILOT'
  | 'DINGTALK_ORGAPP_ROBOT'
  | 'WEIXIN_KF'
  | 'WEIXIN_MP'
  | 'WEIXIN_WORK_APP'
  | 'FEISHU_APP_BOT'
  | 'SUB_FLOW'

export interface AgentLogParams {
  agent_id: string
  user_name?: string
  run_status?: AgentRunStatus
  run_type?: AgentRunType
  page_no: number
  page_size: number
  start_time?: string
  end_time?: string
  application_id?: string
}

export interface AgentLogItem {
  conversation_id: string
  agent_id: string
  title: string
  latest_create_time: string
  total_records: number
  run_status: AgentRunStatus
  run_type: AgentRunType
  user_name: string | null
  user_id: number
  total_upgrade_consume: number
  client_type: 'PC' | 'MOBILE'
}

export interface AgentLogsResponse {
  data: AgentLogItem[]
  total: number
  page_no: number
  page_size: number
}

export interface AgentLogDetailParams {
  conversation_id: string
  agent_id: string
  page_no: number
  page_size: number
  application_id?: string
}

export interface AgentLogDetailItem {
  create_time: string
  file_name: string
  file_type: string
  record_status: AgentRunStatus
  response: string | null
  response_ext: any
  session_id: string
  user_content: string
}

export interface AgentLogDetailResponse {
  data_list: AgentLogDetailItem[]
  page_no: number
  page_size: number
  total_count: number
  total_pages: number
}

export interface AgentVariableConfig {
  label: string
  variable_name: string
  type: 'single_text'
  required: boolean
  placeholder: string
}

export interface AgentRuleConfig {
  channel: LLMChannels
  messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>
  model: string
  stream: boolean
  temperature: number
  welcome_enabled?: boolean
  welcome: string
  rule_id: string
  question_guide: string[]
  prompt_plugins: {
    length_prompt?: number
    time_prompt?: boolean
    [key: string]: any
  }
  agent_variables: AgentVariableConfig[]
  related_questions_enabled: boolean
  related_questions_obj?: {
    related_questions_type?: 'AI' | 'CUSTOM_PROMPT' | 'CLOSE'
    custom_related_questions_prompt?: string
  }
  dataset_global_config?: {
    description: string
    show_knowledge_url: boolean
  }
  enforce_execution?: {
    type?: 'CLOSE' | 'FLOW' | 'DATASET'
    tool_id?: string
  }
}

export interface AgentDataset {
  description: string
  user_description?: string
  id: string
  dataset_id: string
  num: number
  threshold: number
  is_enable: boolean
  partition_id: number
  partition_icon_color: string
  partition_icon: string
  partition_name: string
  partition_category: PartitionCategoryType
  partition_file_count: number
  file_num: number
  hit_strategy: number
  ranking_strategy: number
  show_knowledge_url: boolean
  query_mode?: {
    mode: 'AI' | 'FORCE'
    desc?: string
    tag: boolean
  }
  similarity: number
  random_result: boolean
}

export interface AgentDatasetRequest {
  flow_id: string
  version_id: string
  partition_id: number
  user_description?: string
  description: string
  is_enable: boolean
  num: number
  threshold: number
  hit_strategy: number
  ranking_strategy: number
  show_knowledge_url: boolean
}

export interface AgentSkillBaseRequest {
  flow_id: string // 当前Agent的FlowId
  rel_flow_id: string // 选中的技能的flow_id
  version_id: string
  type: 'flow'
  app_id: string
  inputs?: Array<{
    key: string
    description: string
    default?: string
    required: boolean
    field_type: string
  }>
  output_mode?: string
  outputs: Array<{
    key: string
    value: string
  }>
  description: string
}

export type AgentPluginInputComponent =
  | {
      component: 'slider'
      display_name: string
      limit: { min: number; max: number }
      type: 'int'
      value: number
      required?: boolean
    }
  | {
      component: 'switch'
      display_name: string
      type: 'bool'
      value: boolean
      required?: boolean
      description?: string
    }
  | {
      component: 'card'
      display_name: string
      type: 'string'
      options: {
        label: string
        value: string
        description: string
        consume_points: string
      }[]
      value: string
      required?: boolean
    }
  | {
      component: 'select'
      display_name: string
      options: unknown[]
      required: boolean
      type: 'string'
      value: string
      llm_model?: boolean
    }
  | {
      component: 'input'
      display_name: string
      placeholder: string
      required?: boolean
      type: 'string'
      value?: string | null
    }

export interface AgentPluginOutputComponent {
  component: 'select'
  optional: {
    key: string
    description: string
  }[]
  value: string
}

export interface AgentPluginProperties {
  inputs?: Record<string, AgentPluginInputComponent>
  outputs?: Record<string, AgentPluginOutputComponent>
}

export interface InputParameter {
  // 参数变量名称
  key: string
  // 参数中文名称
  name: string
  description: string | { description: string }
  field_type: string | { type: string }
  required: boolean
  default: any
  example: any
  component?: string
}

export interface PropertyParameter {
  type: string | { type: string }
  description: string | { description: string }
  name: string
  default: any
  example: any
  component?: string
}

export interface ParametersWithInput {
  inputs: InputParameter[]
  output: unknown // 暂时没用到该字段，需要用时自行补全类型
}

export interface ParametersWithProperty {
  type: string
  required: string[]
  properties: Record<string, PropertyParameter>
}

export interface AgentPluginSkill {
  type: 'system'
  id: number
  function_id: string
  name: string
  display_name: string
  description: string
  metadata_config?: {
    icon?: string
    color?: string
    properties?: AgentPluginProperties
  }
  parameters: ParametersWithInput | ParametersWithProperty
  is_new: boolean
  is_deleted: boolean
  created_at: string
  updated_at: string
  consume_mode: string
}

export type AgentRelFlowStatus = 'DELETED' | 'PUBLISHED' | 'DRAFT'

export interface AgentFlowSkill {
  app_id: string
  description: string
  flow_description: string
  flow_id: string
  icon: string
  color: string
  id: string
  inputs: AgentSkillBaseRequest['inputs']
  is_enable: boolean
  name: string
  outputs: AgentSkillBaseRequest['outputs']
  type: 'flow'
  agent_rel_flow_status: AgentRelFlowStatus
  flow_enabled: boolean
  rel_flow_form_config: FieldItem[]
}

export type AgentListSystemSkillItem = Pick<
  AgentPluginSkill,
  'description' | 'function_id' | 'display_name' | 'parameters' | 'type'
> & {
  name: string
  description: string
  metadata: AgentPluginSkill['metadata_config']
  is_enable: boolean
}

export interface AgentSkillListResponse {
  flows: AgentFlowSkill[]
  utility: Array<AgentListSystemSkillItem>
}

export function fetchAgentLogs(params: AgentLogParams) {
  return request.post<AgentLogsResponse>(
    '/v1/chat/conversation/agent_log',
    params,
    {
      headers: { 'Application-Id': params.application_id },
    },
  )
}

export function fetchAgentLogDetail(params: AgentLogDetailParams) {
  return request.get('/v1/chat/agent/log/detail', {
    params,
    headers: { 'Application-Id': params.application_id },
  })
}

export interface AgentDetailTable {
  id: string
  is_enable: boolean
  table_id: string
  title: string
  type: 'database'
}

export interface AgentDetailDatabase {
  icon?: string
  icon_color?: string
  database_id: string
  database_name: string
  source_id: string
  table_list?: AgentDetailTable[]
}

export interface AgentDetailResponse {
  appId: string
  developer: string
  workspace_id: string
  workspace_name: string
  isTemplate: boolean
  application?: {
    id: string
    flowId: string
    color?: string
    name?: string
    icon?: string
    description?: string
    draftVersionId?: string
    createdBy?: string
  }
  database?: AgentDetailDatabase
  dataset?: { file_id: string; file_name: string; mimetype: string }[]
  flows?: AgentFlowSkill[]
  rule?: AgentRuleConfig
  utility?: Array<AgentListSystemSkillItem>
  agent_auth_app: Array<AuthAppWeChat | AuthAppDingTalk>
}

/**
 * @description chat 页获取 bot 详情用
 * @param botId 其实是 flow_id
 */
export function fetchAgentDetail(botId: string) {
  return request.post<AgentDetailResponse>('/v1/agent/detail', {
    flow_id: botId,
  })
}

/**
 * @description 获取Agent规则
 */
export function fetchAgentRule(flow_id: string, version_id?: string) {
  return request.post<AgentRuleConfig>('/v1/agent/rule/detail', {
    flow_id,
    version_id,
  })
}

export function getRelatedQuestionPrompt(flow_id: string, version_id: string) {
  return request.get<string>('/v1/agent/relate_question', {
    params: { flow_id, version_id },
  })
}

/**
 * @description 更新Agent规则
 */
export function updateAgentRule(
  rule: AgentRuleConfig,
  flow: { flow_id: string; version_id: string },
) {
  return request.post('/v1/agent/rule/update', Object.assign(rule, flow))
}

/**
 * @description 获取Agent知识列表
 */
export function fetchAgentDatasetList(flow_id: string, version_id: string) {
  return request.post<AgentDataset[]>('/v1/agent/dataset/list', {
    flow_id,
    version_id,
  })
}

/**
 * @description 创建Agent知识
 */
export function createAgentDataset(data: AgentDatasetRequest) {
  return request.post('/v1/agent/dataset/create', data)
}

export function updateAgentDataset(
  data: Pick<
    AgentDatasetRequest,
    'flow_id' | 'version_id' | 'show_knowledge_url' | 'description'
  >,
) {
  return request.post('/v1/agent/dataset/update', data)
}

export function switchAgentDataset(
  data: AgentDatasetRequest & { dataset_id: string },
) {
  return request.post('/v1/agent/dataset/switch', data)
}

/**
 * @description 解绑agent知识库
 */
export function unbindAgentDataset(data: {
  flow_id: string
  version_id: string
  partition_id: number
}) {
  return request.post('/v1/agent/dataset/untie', data)
}

/**
 * @description 获取Agent技能列表
 */
export function fetchAgentSkillList(
  flow_id: string,
  version_id: string,
  workspace_id: string,
) {
  return request.post<AgentSkillListResponse>('/v1/agent/skill/list', {
    flow_id,
    version_id,
    workspace_id,
  })
}

/**
 * @description 获取Agent系统工具列表
 */
export function fetchAgentFunctionList() {
  return request.post<AgentPluginSkill[]>('/v1/agent/utility/function/list')
}

export function queryAgentPlugin(
  function_id: string,
): Promise<AgentPluginSkill> {
  return request.get(`/v1/agent/utility/${function_id}/detail`)
}

export function updateAgentPlugin(
  function_id: string,
  values: {
    agent_id: string
    description: string
    properties: AgentPluginProperties
    parameters: Record<string, any>
  },
) {
  return request.post(`/v1/agent/utility/${function_id}/update`, {
    ...values,
  })
}

export function createAgentSkill(data: AgentSkillBaseRequest) {
  return request.post('/v1/agent/skill/create', data)
}

export function updateAgentSkill(
  data: AgentSkillBaseRequest & { id?: string },
) {
  return request.post('/v1/agent/skill/update', data)
}

export function deleteAgentSkill(
  id: string,
  flow_id: string,
  version_id: string,
) {
  return request.post('/v1/agent/skill/delete', { id, flow_id, version_id })
}

export function enableAgentSkill(data: {
  id: string
  flow_id: string
  version_id: string
  enabled: boolean
}) {
  return request.post('/v1/agent/skill/enabled', data)
}

export function createAgentSkillFunction(
  function_ids: string[],
  flow_id: string,
  version_id: string,
) {
  return request.post('/v1/agent/utility/function/create', {
    function_ids,
    flow_id,
    version_id,
  })
}

export function deleteAgentSkillFunction(
  function_id: string,
  flow_id: string,
  version_id: string,
) {
  return request.post('/v1/agent/utility/function/delete', {
    function_id,
    flow_id,
    version_id,
  })
}

export function enableAgentSkillFunction(data: {
  flow_id: string
  version_id: string
  function_id: string
  enabled: boolean
}) {
  return request.post('/v1/agent/utility/function/enabled', data)
}

export function getAgentRel(data: { flow_id: string; workspace_id: string }) {
  return request.post('/v1/agent/flow/rel', data)
}

// robot对话记录分享
export function shareAgentContent(conversation_id: string) {
  return request.get(
    `/v1/agent/share/content?conversation_id=${conversation_id}`,
  )
}

// robot对话快照记录分享
export function getConversationListBySnapshotId(snapshot_id: string) {
  return request.get(`/v1/agent/share/conversation/list/${snapshot_id}`)
}

// robot对话记录分享
export function getConversationListByConversationId(
  conversation_id: string,
  robot_id: string,
) {
  return request.get(
    `/v1/chat/records?conversation_id=${conversation_id}&page_number=1&robot_id=${robot_id}`,
  )
}

// robot对话记录取消分享
export function deleteShareAgentContent(snapshot_id: string) {
  return request.get(
    `/v1/agent/delete/share/conversation?snapshot_id=${snapshot_id}`,
  )
}

// 继续此会话
export function continueAgentConversation(snapshot_id: string) {
  return request.get(`/v1/agent/this/conversation?snapshot_id=${snapshot_id}`)
}

// 获取单个会话
export function getConversationById(conversation_id: string) {
  return request.get(`/v1/chat/conversation?conversation_id=${conversation_id}`)
}

// 根据会话id获取快照
export function getSnapshotIdByConversationId(conversation_id: string) {
  return request.get(`/v1/agent/query/snapshotId/${conversation_id}`)
}

export function createDatasetByTemplate(data: {
  flow_id: string
  version_id: string
  type: number
}) {
  return request.post<string>('/v1/agent/dataset/template', data)
}

export function toggleIsRelatedQuestionGenerate(data: {
  conversation_id: string
  session_id: string
  session_related_questions_enabled: boolean
}) {
  return request.post('/v1/agent/session_related_questions', data)
}

interface BindTableForAgentPayload {
  flow_id: AgentId | null
  version_id: string | null
  database_id: string
  table_id: string
  source_id: string
}
export function bindTableForAgent(
  payload: BindTableForAgentPayload,
): Promise<TableType> {
  return request.post('/v1/agent/database/create', payload)
}

export function unbindDatabase(
  flow_id: string,
  version_id: string,
  appId: string,
) {
  return request.post(
    '/v1/agent/database/untie',
    {
      flow_id,
      version_id,
    },
    {
      headers: {
        'Application-Id': appId,
      },
    },
  )
}

interface BindDatabasePayload {
  flow_id: string
  version_id: string
  database_id: string
  source_id: string
}
export function bindDatabase(data: BindDatabasePayload) {
  return request.post('/v1/agent/database/add_existing_database', {
    ...data,
    db_params: null,
  })
}

interface EnableDatabasePayload {
  flow_id: string
  version_id: string
  tool_id: string
  enabled: boolean
}
export function enableTableForAgent(data: EnableDatabasePayload) {
  return request.post('/v1/agent/database/enabled', data)
}

interface DeleteTableForAgentPayload {
  database_id: string
  table_id: string
}
export function deleteTableForAgent(data: DeleteTableForAgentPayload) {
  return request.post('/v1/agent/database/delete', data)
}

interface GetAgentConfigByTypePayload {
  robot_id: string
  run_type: string
}

interface GetAgentConfigByTypeRes {
  conversation_id: string
  config: {
    index: number
    title_suffix: string
    model_config: {
      model: string
      channel: string
      temperature?: number
      top_p?: number
      presence_penalty?: number
      frequency_penalty?: number
      prompt_plugins?: { time_prompt?: boolean; length_prompt?: number }
    }
  }
}

export function getAgentConfigByType(params: GetAgentConfigByTypePayload) {
  return request.get<GetAgentConfigByTypeRes[]>('/v1/chat/conversation/list', {
    params,
  })
}

export function deleteAgentConversation(id: string) {
  return request.delete('/v1/chat/conversation', {
    params: {
      conversation_id: id,
    },
  })
}
